import { parseSelector, SelectorMatcher, ToSelectorParts } from '@fullstory/selectors';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import { useCallback, useMemo } from 'react';
import { NativePlatform, PlatformSelector } from '@fullstory/proto/fs/api/segment';
import { BlockType } from '@fullstory/proto/fs/services/orgs';
import { useNamedElementBlockRuleActions } from '@fullstory/redux';
import { TargetedSelector } from '@fullstory/proto/fs/services/ingestion-settings';

export const useMatchesPrivacyRuleSelector = (
  applicableSelectors: PlatformSelector[] | undefined,
) => {
  const privacyMatcher = usePrivacyRuleMatcher();

  return useMemo(() => {
    // Check if the applicable selectors at least partially matches a privacy rule selector
    if (!applicableSelectors) {
      return false;
    }
    return privacyMatcher(applicableSelectors);
  }, [applicableSelectors, privacyMatcher]);
};

export const usePrivacyRuleMatcher = () => {
  const { blockRules } = useNamedElementBlockRuleActions();

  const matchPrivacyRuleSelector = useCallback(
    (
      platforms: NativePlatform[],
      targetedSelectors: TargetedSelector[],
      platformSelector: PlatformSelector,
    ): boolean =>
      targetedSelectors.some(targetedSelector => {
        // both platforms are filled to check, if not, it means all platforms
        if (platforms.length && platformSelector.platforms.length) {
          const hasMatchingPlatform = platforms.some(platform =>
            platformSelector.platforms.includes(platform),
          );
          if (!hasMatchingPlatform) {
            return false;
          }
        }

        const selectorMatcher = new SelectorMatcher();
        let parsedBaseSelector: SelectorChain;
        let parsedSelector: SelectorChain;
        try {
          parsedBaseSelector = parseSelector(platformSelector.selector);
          parsedSelector = parseSelector(targetedSelector.selector);
        } catch (e) {
          return false;
        }
        const selectorParts = ToSelectorParts(parsedBaseSelector);
        return selectorMatcher.matches(parsedSelector, selectorParts, true);
      }),
    [],
  );

  const matchesPrivacyRuleSelector = useCallback(
    (selectors: PlatformSelector[]): boolean =>
      !!blockRules.length &&
      selectors.some(
        selector =>
          selector &&
          blockRules.find(
            blockRule =>
              blockRule.type !== BlockType.BLOCK_UNMASK &&
              matchPrivacyRuleSelector(blockRule.platforms, blockRule.targetedSelectors, selector),
          ),
      ),
    [matchPrivacyRuleSelector, blockRules],
  );

  return matchesPrivacyRuleSelector;
};
